import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../core/i18n'
import { postService } from '../../core/services'
import * as constants from '../../static/constants'
import { IWindow, IPost, IPostCost } from 'static/interfaces'
import { Button, InputText, InputTextHighlightable } from '../atoms'

declare var window: IWindow

interface IProps {
  post: IPost
}

export const PostCost: React.FC<IProps> = props => {
  const [postCost, setPostCost] = React.useState<IPostCost>(
    props.post.post_cost ||
      ({
        id: null,
        post_id: props.post.id,
        default_travel_cost: constants.DEFAULT_TRAVEL_COST,
        unit_cost: 0,
        other_total_cost: 0,
        special_activity_fee_1: 0,
        special_activity_fee_2: 0,
        special_activity_fee_3: 0,
        special_activity_fee_4: 0,
        special_activity_fee_5: 0,
        special_activity_fee_6: 0,
        special_activity_fee_7: 0,
        special_activity_fee_8: 0,
        special_activity_fee_9: 0,
        special_activity_fee_over_base: 0,
        special_activity_fee_over_unit: 0,
        large_group_min_people: 0,
        large_group_fee: 0,
      } as IPostCost)
  )
  const [changedDefaultTravelCost, setChangedDefaultTravelCost] = React.useState(false)
  const unsetTravelCost = !props.post.post_cost

  const onChangeDefaultTourCost = React.useCallback(
    event => {
      const newValue = Number(event.target.value) || 0
      setPostCost({
        ...postCost,
        default_travel_cost: newValue,
      })

      // 値を一度でも変更したら、未設定表示のみ消す（ハイライトはまだ未保存であることを伝えるため残す）
      if (unsetTravelCost && !changedDefaultTravelCost) {
        setChangedDefaultTravelCost(true)
      }
    },
    [postCost, unsetTravelCost, changedDefaultTravelCost]
  )

  const handleSubmit = React.useCallback(async () => {
    const params = {
      post_cost: {
        id: postCost.id,
        post_id: props.post.id,
        default_travel_cost: postCost.default_travel_cost,
        unit_cost: postCost.unit_cost,
        other_total_cost: postCost.other_total_cost,
        special_activity_fee_1: postCost.special_activity_fee_1,
        special_activity_fee_2: postCost.special_activity_fee_2,
        special_activity_fee_3: postCost.special_activity_fee_3,
        special_activity_fee_4: postCost.special_activity_fee_4,
        special_activity_fee_5: postCost.special_activity_fee_5,
        special_activity_fee_6: postCost.special_activity_fee_6,
        special_activity_fee_7: postCost.special_activity_fee_7,
        special_activity_fee_8: postCost.special_activity_fee_8,
        special_activity_fee_9: postCost.special_activity_fee_9,
        special_activity_fee_over_base: postCost.special_activity_fee_over_base,
        special_activity_fee_over_unit: postCost.special_activity_fee_over_unit,
        large_group_min_people: postCost.large_group_min_people,
        large_group_fee: postCost.large_group_fee,
      },
    }
    try {
      const { flush } = await (postCost.id
        ? postService.updatePostCost(postCost.id, params)
        : postService.createPostCost(params))
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      window.location.reload()
    } catch (error) {
      console.error(error)
    }
  }, [props.post, postCost])

  return (
    <Wrapper>
      <h2 className="Editor_Title">{I18n.t('activerecord.models.post_cost')}</h2>
      <div className="FormItmes">
        <FormItem>
          <InputText
            disabled={true}
            readonly={true}
            name="id"
            label={I18n.t('generic.id')}
            value={postCost.id}
          />
        </FormItem>
        <FormItem>
          <InputText
            disabled={true}
            readonly={true}
            name="post_id"
            label={I18n.t('activerecord.attributes.post_cost.post_id')}
            value={postCost.post_id}
          />
        </FormItem>
        <FormItem>
          <InputTextHighlightable
            type="number"
            required={true}
            name="default_travel_cost"
            label={I18n.t('activerecord.attributes.post_cost.default_travel_cost')}
            placeholder={I18n.t('activerecord.attributes.post_cost.default_travel_cost')}
            highlight={unsetTravelCost}
            customMessage={unsetTravelCost && !changedDefaultTravelCost ? '未設定' : null}
            value={postCost.default_travel_cost}
            onChangeHandler={onChangeDefaultTourCost}
          />
        </FormItem>
        <FormItem>
          <InputText
            type="number"
            required={true}
            name="unit_cost"
            label={I18n.t('activerecord.attributes.post_cost.unit_cost')}
            placeholder={I18n.t('activerecord.attributes.post_cost.unit_cost')}
            value={postCost.unit_cost}
            onChangeHandler={event => {
              setPostCost({
                ...postCost,
                unit_cost: Number(event.target.value) || 0,
              })
            }}
          />
        </FormItem>
        <FormItem>
          <InputText
            type="number"
            required={true}
            name="other_total_cost"
            label={I18n.t('activerecord.attributes.post_cost.other_total_cost')}
            placeholder={I18n.t('activerecord.attributes.post_cost.other_total_cost')}
            value={postCost.other_total_cost}
            onChangeHandler={event => {
              setPostCost({
                ...postCost,
                other_total_cost: Number(event.target.value) || 0,
              })
            }}
          />
        </FormItem>
        <div className="SpecialActivityFee">
          <div className="SpecialActivityFee_Title">
            {I18n.t('activerecord.attributes.post_cost.special_activity_fee')}
          </div>
          <div className="SpecialActivityFee_Items">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => {
              const feeKey = `special_activity_fee_${num}` as keyof IPostCost
              return (
                <FormItem key={num}>
                  <InputText
                    type="number"
                    required={true}
                    name={`special_activity_fee_${num}`}
                    label={I18n.t(`activerecord.attributes.post_cost.special_activity_fee_${num}`)}
                    placeholder={I18n.t(
                      `activerecord.attributes.post_cost.special_activity_fee_${num}`
                    )}
                    value={postCost[feeKey] as number}
                    onChangeHandler={event => {
                      setPostCost({
                        ...postCost,
                        [feeKey]: Number(event.target.value) || 0,
                      })
                    }}
                  />
                </FormItem>
              )
            })}
            <FormItem>
              <InputText
                type="number"
                required={true}
                name="special_activity_fee_over_base"
                label={I18n.t('activerecord.attributes.post_cost.special_activity_fee_over_base')}
                placeholder={I18n.t(
                  'activerecord.attributes.post_cost.special_activity_fee_over_base'
                )}
                value={postCost.special_activity_fee_over_base}
                onChangeHandler={event => {
                  setPostCost({
                    ...postCost,
                    special_activity_fee_over_base: Number(event.target.value) || 0,
                  })
                }}
              />
            </FormItem>
            <FormItem>
              <InputText
                type="number"
                required={true}
                name="special_activity_fee_over_unit"
                label={I18n.t('activerecord.attributes.post_cost.special_activity_fee_over_unit')}
                placeholder={I18n.t(
                  'activerecord.attributes.post_cost.special_activity_fee_over_unit'
                )}
                value={postCost.special_activity_fee_over_unit}
                onChangeHandler={event => {
                  setPostCost({
                    ...postCost,
                    special_activity_fee_over_unit: Number(event.target.value) || 0,
                  })
                }}
              />
            </FormItem>
          </div>
        </div>
        <div className="LargeGroup">
          <div className="LargeGroup_Title">
            {I18n.t('activerecord.attributes.post_cost.large_group')}
          </div>
          <div className="LargeGroup_Items">
            <FormItem>
              <InputText
                type="number"
                required={true}
                name="large_group_min_people"
                label={I18n.t('activerecord.attributes.post_cost.large_group_min_people')}
                placeholder={I18n.t('activerecord.attributes.post_cost.large_group_min_people')}
                value={postCost.large_group_min_people}
                onChangeHandler={event => {
                  setPostCost({
                    ...postCost,
                    large_group_min_people: Number(event.target.value) || 0,
                  })
                }}
              />
            </FormItem>
            <FormItem>
              <InputText
                type="number"
                required={true}
                name="large_group_fee"
                label={I18n.t('activerecord.attributes.post_cost.large_group_fee')}
                placeholder={I18n.t('activerecord.attributes.post_cost.large_group_fee')}
                value={postCost.large_group_fee}
                onChangeHandler={event => {
                  setPostCost({
                    ...postCost,
                    large_group_fee: Number(event.target.value) || 0,
                  })
                }}
              />
            </FormItem>
          </div>
        </div>
        <div className="Editor_Footer">
          <Button
            handleClick={handleSubmit}
            textColor={constants.COLORS.white}
            backgroundColor={constants.COLORS.primary}
          >
            {I18n.t(postCost.id ? 'generic.update' : 'generic.create')}
          </Button>
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding-bottom: 14px;
  .FormItmes {
    margin: 14px 16px;
  }
  .SpecialActivityFee {
    display: flex;
    align-items: baseline;
    margin-top: 8px;
    .SpecialActivityFee_Title {
      font-size: 14px;
      font-weight: bold;
      width: 180px;
    }
    .SpecialActivityFee_Items {
      display: flex;
      flex-direction: column;
    }
  }
  .LargeGroup {
    display: flex;
    align-items: baseline;
    margin-top: 8px;
    .LargeGroup_Title {
      font-size: 14px;
      font-weight: bold;
      width: 180px;
    }
    .LargeGroup_Items {
      display: flex;
      flex-direction: column;
    }
  }
`
const FormItem = styled.div`
  & + & {
    margin-top: 16px;
  }

  .FormItem_Title {
    font-size: 15px;
    font-weight: bold;
  }
`

export default PostCost
